import { useCallback, useEffect, useRef, useState } from "react";

import { useDeployment, useWallet } from "../../context";
import { STATE_VIEW_ABI } from "../../lib/abis";
import { sortTokens, ZERO_ADDRESS } from "../../lib/constants";

import { computeV4PoolId } from "./types";

import type { V4Position, V4PositionDescriptor } from "./types";
import type { Address, Hex } from "viem";

const STORAGE_KEY = "devenv-v4-positions";
const ZERO_BYTES32 =
  "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex;

/** Build a deterministic key for dedup. */
function descriptorKey(d: V4PositionDescriptor): string {
  return `${d.currency0.toLowerCase()}:${d.currency1.toLowerCase()}:${String(d.fee)}:${String(d.tickSpacing)}:${d.hooks.toLowerCase()}:${String(d.tickLower)}:${String(d.tickUpper)}:${d.salt}`;
}

function loadDescriptors(): V4PositionDescriptor[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as V4PositionDescriptor[];
  } catch {
    return [];
  }
}

function saveDescriptors(descs: V4PositionDescriptor[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(descs));
}

export function useV4Positions() {
  const { deployment } = useDeployment();
  const { publicClient } = useWallet();

  const descriptorsRef = useRef<Map<string, V4PositionDescriptor>>(new Map());
  const [positions, setPositions] = useState<V4Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [poolTicks, setPoolTicks] = useState<Record<string, number>>({});
  const [initialized, setInitialized] = useState(false);

  // Seed known positions + load from localStorage on deployment change
  useEffect(() => {
    if (!deployment?.tokens) return;

    const tokens = deployment.tokens as unknown as Record<string, Address>;
    const weth = tokens.weth;
    const usdc = tokens.usdc;
    const dai = tokens.dai;

    const seedDescriptors: V4PositionDescriptor[] = [];

    if (weth && usdc) {
      const [c0, c1] = sortTokens(weth, usdc);
      seedDescriptors.push({
        currency0: c0,
        currency1: c1,
        fee: 3000,
        tickSpacing: 60,
        hooks: ZERO_ADDRESS,
        tickLower: -887220,
        tickUpper: 887220,
        salt: ZERO_BYTES32,
      });
    }

    if (weth && dai) {
      const [c0, c1] = sortTokens(weth, dai);
      seedDescriptors.push({
        currency0: c0,
        currency1: c1,
        fee: 3000,
        tickSpacing: 60,
        hooks: ZERO_ADDRESS,
        tickLower: -887220,
        tickUpper: 887220,
        salt: ZERO_BYTES32,
      });
    }

    // Merge seeds + localStorage
    const map = new Map<string, V4PositionDescriptor>();
    for (const d of seedDescriptors) {
      map.set(descriptorKey(d), d);
    }
    for (const d of loadDescriptors()) {
      map.set(descriptorKey(d), d);
    }
    descriptorsRef.current = map;
    setInitialized(true);
  }, [deployment?.tokens]);

  const fetchPositions = useCallback(async () => {
    if (descriptorsRef.current.size === 0) return;

    setLoading(true);

    try {
      if (!deployment) return;
      const stateView = deployment.v4.stateView;
      const modLiq = deployment.v4.poolModifyLiquidityTest;
      const descriptors = [...descriptorsRef.current.values()];

      // Build per-descriptor poolId and poolKey
      const descriptorsWithMeta = descriptors.map((desc) => ({
        desc,
        poolId: computeV4PoolId(
          desc.currency0,
          desc.currency1,
          desc.fee,
          desc.tickSpacing,
          desc.hooks,
        ),
        poolKey: `${desc.currency0.toLowerCase()}:${desc.currency1.toLowerCase()}:${String(desc.fee)}`,
      }));

      // Phase 1: Fetch all unique pool slot0s in parallel
      const uniquePools = new Map<
        string,
        { poolId: ReturnType<typeof computeV4PoolId>; poolKey: string }
      >();
      for (const item of descriptorsWithMeta) {
        if (!uniquePools.has(item.poolKey)) {
          uniquePools.set(item.poolKey, {
            poolId: item.poolId,
            poolKey: item.poolKey,
          });
        }
      }

      const slot0Results = await Promise.allSettled(
        [...uniquePools.values()].map(async ({ poolId, poolKey }) => {
          const slot0 = (await publicClient.readContract({
            address: stateView,
            abi: STATE_VIEW_ABI,
            functionName: "getSlot0",
            args: [poolId],
          })) as readonly [bigint, number, number, number];
          return { poolKey, slot0 };
        }),
      );

      const ticks: Record<string, number> = {};
      for (const r of slot0Results) {
        if (r.status === "fulfilled" && r.value.slot0[0] > 0n) {
          ticks[r.value.poolKey] = r.value.slot0[1];
        }
      }

      // Phase 2: Fetch all position infos in parallel
      const posResults = await Promise.allSettled(
        descriptorsWithMeta.map(async ({ desc, poolId }) => {
          const info = (await publicClient.readContract({
            address: stateView,
            abi: STATE_VIEW_ABI,
            functionName: "getPositionInfo",
            args: [poolId, modLiq, desc.tickLower, desc.tickUpper, desc.salt],
          })) as readonly [bigint, bigint, bigint];
          return { desc, info };
        }),
      );

      const result: V4Position[] = [];
      for (const r of posResults) {
        if (r.status !== "fulfilled") continue;
        const { desc, info } = r.value;
        const liquidity = info[0];
        if (liquidity > 0n) {
          result.push({
            id: descriptorKey(desc),
            currency0: desc.currency0,
            currency1: desc.currency1,
            fee: desc.fee,
            tickSpacing: desc.tickSpacing,
            hooks: desc.hooks,
            tickLower: desc.tickLower,
            tickUpper: desc.tickUpper,
            salt: desc.salt,
            liquidity,
            feeGrowthInside0LastX128: info[1],
            feeGrowthInside1LastX128: info[2],
          });
        }
      }

      setPositions(result);
      setPoolTicks((prev) => {
        const keys = Object.keys(ticks);
        if (
          keys.length === Object.keys(prev).length &&
          keys.every((k) => prev[k] === ticks[k])
        ) {
          return prev;
        }
        return ticks;
      });
    } catch {
      // Batch read failed
    } finally {
      setLoading(false);
    }
  }, [deployment?.v4, publicClient]);

  // Fetch on init and when deployment changes
  useEffect(() => {
    if (initialized) {
      void fetchPositions();
    }
  }, [initialized, fetchPositions]);

  const registerPosition = useCallback(
    (desc: V4PositionDescriptor) => {
      const key = descriptorKey(desc);
      if (descriptorsRef.current.has(key)) {
        // Already tracked — just refetch to update liquidity
        void fetchPositions();
        return;
      }
      descriptorsRef.current.set(key, desc);
      // Persist all descriptors
      saveDescriptors([...descriptorsRef.current.values()]);
      void fetchPositions();
    },
    [fetchPositions],
  );

  return {
    positions,
    loading,
    poolTicks,
    refetch: fetchPositions,
    registerPosition,
  };
}
